import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Loader2, Clock, ArrowRight, BookOpen } from "lucide-react";
import { api } from "../lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

function injectJsonLd(obj, id) {
  if (typeof document === "undefined") return;
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(obj);
}

export default function BlogIndexPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/blogs")
      .then((r) => setBlogs(r.data.blogs || []))
      .catch(() => setBlogs([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!blogs.length) return;
    injectJsonLd(
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "NIBRO by ARAMFIX — Engineering Knowledge Base",
        description:
          "Premium technical B2B articles for engineers, fabricators and procurement specialists working with stainless pipe clamps, hygienic supports and orbital welding.",
        url: `${BASE}/blog`,
        blogPost: blogs.map((b) => ({
          "@type": "BlogPosting",
          headline: b.title,
          description: b.meta_description,
          datePublished: b.published_at,
          url: `${BASE}/blog/${b.slug}`,
        })),
      },
      "ld-blog-index",
    );
    return () => {
      const el = document.getElementById("ld-blog-index");
      if (el) el.remove();
    };
  }, [blogs]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="blog-loading">
        <Loader2 className="animate-spin text-nibro-sage-dark" />
      </div>
    );
  }

  return (
    <div data-testid="blog-index-page">
      <Helmet>
        <title>Engineering Knowledge Base — NIBRO by ARAMFIX</title>
        <meta
          name="description"
          content="Premium technical articles on stainless pipe clamps, hygienic supports, orbital welding and pipe sizing — by NIBRO's process piping engineers."
        />
        <link rel="canonical" href={`${BASE}/blog`} />
      </Helmet>

      {/* Hero */}
      <section
        className="border-b-2 border-aramfix-red/30 px-5 py-20 lg:px-10 lg:py-28"
        style={{ backgroundColor: "var(--nibro-sage)" }}
      >
        <div className="mx-auto max-w-[1200px] text-white">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
            <BookOpen size={14} /> Engineering Knowledge Base
          </div>
          <h1 className="max-w-3xl text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl">
            Technical insight from the NIBRO welding floor.
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/85 sm:text-lg">
            Twenty premium B2B articles for engineers, fabricators, OEMs and procurement teams.
            Real numbers, real tolerances, real installation economics — written by people who
            grind, weld and ship stainless every day from Oisterwijk, NL.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1200px] px-5 py-16 lg:px-10 lg:py-20">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {blogs.map((b, i) => (
            <Link
              key={b.slug}
              to={`/blog/${b.slug}`}
              data-testid={`blog-card-${b.slug}`}
              className="group flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-zinc-100">
                <img
                  src={b.hero_image}
                  alt={b.title}
                  loading={i < 3 ? "eager" : "lazy"}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <span
                  className="absolute left-4 top-4 rounded-full bg-aramfix-red px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white"
                >
                  {b.category}
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-3 p-6">
                <h2 className="text-lg font-bold text-zinc-900 group-hover:text-aramfix-red">
                  {b.title}
                </h2>
                <p className="line-clamp-3 text-sm text-zinc-600">{b.meta_description}</p>
                <div className="mt-auto flex items-center justify-between border-t border-zinc-100 pt-4 text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={12} /> {b.reading_time_min} min read
                  </span>
                  <span className="inline-flex items-center gap-1 font-semibold text-nibro-sage-dark group-hover:text-aramfix-red">
                    Read article <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
